import * as XLSX from "xlsx";
import { meetingValidationRules } from "./meetingValidationConfig";

export const validateMeetingExcel = async (file, serviceSlug) => {
  const rules = meetingValidationRules[serviceSlug];

  if (!rules) {
    return { valid: true };
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

  if (!rows || rows.length < 2) {
    return { valid: false, message: "Excel file is empty" };
  }

  const headers = rows[0];

  // Header validation
  if (headers[0] !== "Field") {
    return {
      valid: false,
      message: "Invalid template. First column must be 'Field'"
    };
  }

  if (serviceSlug !== "board-meeting-minutes" && headers[1] !== "Input") {
    return {
      valid: false,
      message: "Invalid template. Second column must be 'Input'"
    };
  }

  const errors = [];

  // Extract template fields
  const templateFields = rows
    .slice(1)
    .map(r => r[0])
    .filter(Boolean);

  // Detect missing fields
  const missingFields = rules.requiredFields.filter(
    f => !templateFields.includes(f)
  );

  if (missingFields.length > 0) {
    return {
      valid: false,
      message: `Invalid template for ${serviceSlug}. Missing fields: ${missingFields
        .slice(0, 3)
        .join(", ")}`
    };
  }
  const allowed = rules.allowedFields || rules.requiredFields;

  const extraFields = templateFields.filter(
    f => !allowed.includes(f)
  );
  console.log(extraFields)

  if (extraFields.length > 5) {
    return {
      valid: false,
      message: `This file appears to be a different meeting template. Unexpected fields detected.`
    };
  }

  for (let i = 1; i < rows.length; i++) {
    const field = rows[i][0];

    if (!field) continue;

    const values = rows[i].slice(1).filter(v => v !== undefined && v !== "");

    // Required validation
    if (rules.requiredFields.includes(field) && values.length === 0) {
      errors.push(`${field} is required`);
      continue;
    }

    for (const value of values) {
      if (!value) continue;

      // Date validation
      if (rules.dateFields.includes(field)) {
        const date = new Date(value);

        if (isNaN(date.getTime())) {
          errors.push(`${field} must be a valid date`);
        }
      }

      // Time validation
      if (rules.timeFields.includes(field)) {
        let isValidTime = false;

        if (typeof value === 'number') {
          // Excel stores times as numeric fractions (or Date+Time numbers)
          isValidTime = true;
        } else {
          const timeStr = String(value).trim();
          // Support 24h, 12h formats, and AM/PM
          const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9](\s*(AM|PM|am|pm))?$/;
          if (timeRegex.test(timeStr)) {
            isValidTime = true;
          } else {
            // Check if it's a parseable date string with a time component
            const parsedDate = new Date(timeStr);
            if (!isNaN(parsedDate.getTime()) && timeStr.includes(':')) {
              isValidTime = true;
            }
          }
        }

        if (!isValidTime) {
          errors.push(`${field} must be in HH:MM format (e.g., 16:30, 2:15 PM)`);
        }
      }

      // Yes/No validation
      if (rules.yesNoFields.includes(field)) {
        const val = String(value).toLowerCase();

        if (!["yes", "no"].includes(val)) {
          errors.push(`${field} must be Yes or No`);
        }
      }

      // Number validation
      if (rules.numberFields.includes(field)) {
        if (isNaN(value)) {
          errors.push(`${field} must be a number`);
        }
      }
    }
  }

  if (errors.length > 0) {
    return {
      valid: false,
      message: errors.slice(0, 5).join(", ")
    };
  }

  return { valid: true };
};